import {
  LinkTemplate,
  PlacementType,
  SortMode,
  SortOrder,
  TextTemplate,
  TitleHeaderTemplate,
  useNotifications,
} from '@amzn/aces-communications-api';
import { UseAcesBannersMetricsPublisher } from '../../../../commons/utils/metrics';
import { logger } from '../../../../commons/utils/logger';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectChromeConfiguration } from '../../../../app/slice';
import { LocalNotification, UseAcesBannersResponse } from './types';
import { useInternalChromeConfigContext } from 'src/providers/InternalChromeConfigContextProvider';

const BANNER_SEARCH_PARAMS = {
  page: { size: 10, current: 0 },
  sort: { sortBy: SortMode?.MESSAGE_TYPE, sortDirection: SortOrder?.DESC },
  filter: {},
};

const SUPPORTED_ACES_MSG_TYPES = {
  INFORMATION: 'info',
  WARNING: 'warning',
  ERROR: 'error',
};

export const useAcesBanners = (
  placement: PlacementType,
): UseAcesBannersResponse => {
  const [localNotifications, setLocalNotifications] = useState<
    LocalNotification[]
  >([]);
  const { config: internalChromeConfig } = useInternalChromeConfigContext();
  const chromeConfiguration = useSelector(selectChromeConfiguration);
  let isGlobal = false;
  let isAggregated = false;

  if (
    placement === PlacementType.PAGE_BANNER &&
    chromeConfiguration.isAggregated &&
    chromeConfiguration.isGlobal
  ) {
    // isGlobal as true is only supported by PAGE_BANNER, any other placement type will result in an error. Use with caution.
    isGlobal = chromeConfiguration.isGlobal;
    // isAggregated flag only useful on PAGE_BANNER type. Any other types is either ignored or will result in unintended data. Change code with caution.
    isAggregated = chromeConfiguration.isAggregated;
  }

  const searchParams = useMemo(
    () => ({
      ...BANNER_SEARCH_PARAMS,
      isGlobal,
      isAggregated,
      filter: {
        webpageId: chromeConfiguration.pageId,
        applicationName: internalChromeConfig?.applicationName,
      },
    }),
    [
      chromeConfiguration.pageId,
      internalChromeConfig?.applicationName,
      isGlobal,
      isAggregated,
    ],
  );

  const accountParams = useMemo(
    () => ({
      entityId: internalChromeConfig?.entityId,
    }),
    [internalChromeConfig?.entityId],
  );

  const [notifications, , loading, actions] = useNotifications(
    placement,
    searchParams,
    accountParams,
  );

  const mergedActions = useMemo(
    () => ({
      ...actions,
      dismiss: (bannerObjToUpdate) => {
        const notificationsToUpdate = notifications?.find(
          (n) => n.eventId === bannerObjToUpdate.id,
        );
        try {
          if (notificationsToUpdate) {
            return actions.dismiss(notificationsToUpdate);
          } else {
            throw new Error('Banner notification not found');
          }
        } catch (error: any) {
          UseAcesBannersMetricsPublisher.publishCounterMonitor(
            `dismissACESBannerFailure:${bannerObjToUpdate.id}`,
            1,
          );
          logger.error(
            `dismiss aces banner failed for: ${bannerObjToUpdate.id}`,
            error,
            { errorInfo: 'aces-dismiss' },
          );
        }
        return Promise.reject();
      },
    }),
    [actions, notifications],
  );

  useEffect(() => {
    if (loading) {
      return;
    }
    // only support INFORMATION, ERROR, WARNING messageType:
    // https://w.amazon.com/bin/view/Advertising_Console/Advertising_Console_Experience/Console_Banners
    // skip other messageTypes

    const parsedNotifications: LocalNotification[] = notifications
      ?.filter((item) => SUPPORTED_ACES_MSG_TYPES[item?.messageType])
      ?.map((item) => {
        const title = item.components?.find(
          (component) => component.type === 'TitleHeader',
        ) as TitleHeaderTemplate;
        const messageType = SUPPORTED_ACES_MSG_TYPES[item.messageType];
        const acesBannerContent = item.components?.filter(
          (component) => component.type === 'Text' || component.type === 'Link',
        ) as (TextTemplate | LinkTemplate)[];

        return {
          id: item.eventId,
          eventId: item.eventId, // bannerItem shape will have eventId only when data is returned from ACES API
          type: messageType,
          title: title?.text,
          acesBannerContent,
          notificationType: 'aces',
        };
      });

    setLocalNotifications(parsedNotifications);
  }, [notifications, loading]);

  return {
    notifications: localNotifications,
    actions: mergedActions,
    loading: loading,
  };
};
